import { Group } from '@visx/group';
import clsx from 'clsx';
import { type MouseEvent as ReactMouseEvent } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import {
  HIGHLIGHTED_SEGMENT_VALUE,
  INACTIVE_SEGMENT_VALUE,
} from './types/default.js';
import { getTokenComputedStyle } from '../../../../core/utils/getTokenComputedStyle.js';
import { getValidColor } from '../../../../core/utils/getValidColor.js';
import { DEFAULT_GAP_WIDTH, METER_BAR_SIZE } from '../../constants.js';
import { useMeterBarLayoutSize } from '../../context/MeterBarLayoutSize.context.js';
import { useTooltipHandlers } from '../../hooks/use-tooltip-handlers.js';
import { elementSize } from '../../styles/element-size.sty.js';
import { getDashArray } from '../../utils/get-dash-array.js';

interface MeterBarSegmentProps {
  color: string;
  width: number;
  highlighted?: boolean;
  start?: number;
  onMouseClick?: (event?: ReactMouseEvent<SVGGElement>) => void;
  onMouseEnter?: (event?: ReactMouseEvent<SVGGElement>) => void;
  onMouseMove?: (event?: ReactMouseEvent<SVGGElement>) => void;
}

export const MeterBarSegment = (props: MeterBarSegmentProps) => {
  const {
    color,
    width,
    highlighted = true,
    start = 0,
    onMouseClick,
    onMouseEnter,
    onMouseMove,
  } = props;

  const validColor = getTokenComputedStyle(getValidColor(color));

  const { size, segmentHeight } = useMeterBarLayoutSize();
  const {
    onMouseClickHandler,
    onMouseEnterHandler,
    onMouseLeaveHandler,
    onMouseMoveHandler,
  } = useTooltipHandlers();

  const dashArray = getDashArray(width, segmentHeight);

  const isSegmentHeight = size === METER_BAR_SIZE.Auto;

  return (
    <Group
      onMouseEnter={(event) => {
        onMouseEnterHandler();
        onMouseEnter && onMouseEnter(event);
      }}
      onMouseLeave={onMouseLeaveHandler}
      onMouseMove={(event) => {
        onMouseMoveHandler(event);
        onMouseMove && onMouseMove(event);
      }}
      onClick={(event) => {
        onMouseClickHandler(event);
        onMouseClick && onMouseClick(event);
      }}
    >
      <rect
        className={clsx({ [elementSize({ size })]: !isSegmentHeight })}
        x={start}
        y="0"
        height={isSegmentHeight ? segmentHeight : undefined}
        width={width}
        fill={validColor}
        opacity={
          highlighted ? HIGHLIGHTED_SEGMENT_VALUE : INACTIVE_SEGMENT_VALUE
        }
        data-testid="meterbar-segment"
        stroke={Colors.Background.Base.Default}
        strokeWidth={DEFAULT_GAP_WIDTH}
        strokeDasharray={dashArray}
      />
    </Group>
  );
};
